<template>
  <div>
    <div class="slo-number-input">
      <button
        v-show="togglesVisible"
        @click="decrement"
        :disabled="decrementDisabled"
        type="button"
        class="decrement-button"
      >
        <SloIcon name="Minus" />
      </button>
      <input
        type="number"
        v-model="total"
        @blur="handleChange"
        @keydown="handleKeydown"
        @keyup="handleKeyup"
        :style="{'cursor': preventInput ? 'not-allowed' : 'auto'}"
      >
      <button
        v-show="togglesVisible"
        @click="increment"
        :disabled="incrementDisabled"
        type="button"
        class="increment-button"
      >
        <SloIcon name="Plus" />
      </button>
    </div>
    <div v-if="errorText" class="slo-number-input--error-message">
      <SloIcon name="CircleExclamation" />
      <span>{{ errorText }}</span>
    </div>
  </div>
</template>

<script>
import t from '../../js/main/i18n';
import SloIcon from './SloIcon.vue';

export default {
  name: 'SloNumberInput',
  components: {
    SloIcon
  },
  props: {
    initialValue: {type: String|Number, default: 0},
    min: {type: Number, required: true},
    minErrorText: {type: String},
    max: {type: Number, required: true},
    maxErrorText: {type: String},
    preventInput: {type: Boolean, default: false}
  },
  data() {
    return {
      total: 0,
      minExceeded: false,
      maxExceeded: false,
      togglesVisible: true
    };
  },
  created() {
    this.total = this.initialValue;

    window.addEventListener('keyup', e => {
      if (e.key === 'Enter') this.handleChange();
    });
  },
  computed: {
    decrementDisabled() {
      return this.total <= this.min || !this.togglesVisible;
    },
    incrementDisabled() {
      return this.total >= this.max || !this.togglesVisible;
    },
    minReachedOrExceeded() {
      return this.total <= this.min;
    },
    maxReachedOrExceeded() {
      return this.total >= this.max;
    },
    errorText() {
      if (this.minExceeded && this.minErrorText) {
        return this.minErrorText;
      }
      if (this.minExceeded && !this.minErrorText) {
        return t('Min value exceeded');
      }

      if (this.maxExceeded && this.maxErrorText) {
        return this.maxErrorText;
      }
      if (this.maxExceeded && !this.maxErrorText) {
        return t('Max value exceeded');
      }

      return false;
    }
  },
  watch: {
    initialValue(newValue) {
      this.total = newValue;
    },
    total(newValue) {
      if (newValue) {
        if (this.total >= this.min) this.minExceeded = false;
        if (this.total <= this.max) this.maxExceeded = false;
      }
    }
  },
  methods: {
    decrement() {
      if (this.minReachedOrExceeded) {
        this.minExceeded = true;
        return;
      }
      this.total--;
      this.handleChange();
    },
    increment() {
      if (this.maxReachedOrExceeded) {
        this.maxExceeded = true;
        return;
      }
      this.total++;
      this.handleChange();
    },
    handleChange() {
      if (this.total < this.min) {
        this.minExceeded = true;
        return;
      }
      if (this.total > this.max) {
        this.maxExceeded = true;
        return;
      }

      this.togglesVisible = true;
      this.$emit('valueUpdate', parseInt(this.total));
    },
    handleKeydown() {
      this.togglesVisible = false;
    },
    handleKeyup() {
      this.$emit('inputUpdate', parseInt(this.total));
    }
  }
};
</script>

<style lang="scss" scoped>
@import '../../scss/system';

$border: 1px solid #d1d5db;

.slo-number-input {
  display: flex;
  align-items: center;
  border: $border;
  border-radius: 6px;
  transition: border-color 0.15s ease-in;

  &:focus-within {
    border-color: $color-blue-400;
  }

  button {
    border: none;
    background: none;
    padding: 1.2rem;
    cursor: pointer;
    outline: none;
    color: #9ca3af;

    &:hover {
      color: $color-gray-700;
    }

    &:disabled {
      pointer-events: none;
      color: $color-gray-400;
    }
  }

  input {
    border: none;
    text-align: center;
    width: 100%;
    background-color: transparent;
    padding: 0;
  }

  input[type='number'] {
    -moz-appearance: textfield;
    -webkit-appearance: textfield;
    appearance: textfield;
  }

  &--error-message {
    @include text('sm');
    position: absolute;
    display: flex;
    align-items: center;
    gap: 0.8rem;
    width: 100%;
    max-width: max-content;
    margin-inline: auto;
    margin-top: 4px;
    padding: 0.7rem;
    background-color: #fff;
    color: $color-red-400;
    border: 1px solid $color-red-400;
    border-radius: 6px;
  }

  @media (min-width: 768px) {
    max-width: 125px;
  }
}

.mini-cart--body {
  .slo-number-input--error-message {
    position: absolute;
    left: 0;
    right: 0;
    max-width: 95%;
    z-index: 10;

    @media (min-width: 480px) {
      max-width: max-content;
    }
  }
}

.marketplace-full-cart--cart-products {
  .slo-number-input--error-message {
    position: absolute;
    right: 0;
    z-index: 10;

    @media (min-width: 992px) {
      left: 120px;
    }
  }
}

.parts-list-item {
  .slo-number-input--error-message {
    @media (min-width: 1200px) {
      right: 0;
      width: auto;
      white-space: nowrap;
    }
  }
}

.mini-cart--upsell-carousel {
  .slo-number-input--error-message {
    top: -48px;

    @media (min-width: 768px) {
      top: -72px;
      left: 0;
    }
  }
}
</style>
